"use client";
import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const scrolled = window.scrollY;
      const bg = sectionRef.current.querySelector(".hero-bg") as HTMLElement;
      if (bg) bg.style.transform = `translateY(${scrolled * 0.3}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      id="hero">

      {/* Background */}
      <div className="hero-bg absolute inset-0 z-0 overflow-hidden">
        <div className="ken-burns w-full h-full">
          <AppImage
            src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4"
            alt="Panoramic view of Kashmir valley with snow-capped Himalayan mountains, lush green meadows and pristine landscape"
            fill
            className="object-cover w-full h-full"
            priority />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-linen/80 via-linen/65 to-linen/90" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-3xl mx-auto px-6 flex flex-col items-center text-center pt-32 pb-24">
        {/* Eyebrow */}
        <div className="flex items-center gap-2 mb-6">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber" />
          </span>
          <span className="text-xs font-medium tracking-[0.2em] uppercase text-pine-light">
            Premium Land Investment · Kashmir, India
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-serif font-light text-display text-pine leading-[0.95] mb-5">
          Own Land in Kashmir.<br />
          <em className="italic">Build Wealth That Lasts.</em>
        </h1>
        <p className="text-base text-pine-light font-light max-w-lg leading-relaxed mb-10">
          Kashmir is one of India&apos;s most undervalued land markets. We help you invest with confidence — verified plots, legal clarity, and expert guidance from day one.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-14">
          <a
            href="#properties"
            className="bg-amber text-linen text-sm font-medium px-8 py-4 rounded-full hover:bg-amber-hover transition-colors duration-300 whitespace-nowrap">
            View Properties
          </a>
          <a
            href="https://wa.link/a6acna"
            target="_blank"
            rel="noopener noreferrer"
            className="border border-pine/30 text-pine text-sm font-medium px-8 py-4 rounded-full hover:bg-pine/5 transition-colors duration-300 whitespace-nowrap">
            Book Consultation
          </a>
        </div>

        {/* Floating stats */}
        <div className="flex items-center gap-8 flex-wrap justify-center">
          {[
            { value: "200+", label: "Verified plots listed" },
            { value: "₹15L+", label: "Entry-level investments" },
            { value: "12+", label: "Years of local expertise" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-serif text-2xl font-medium text-pine">{stat.value}</div>
              <div className="text-xs text-pine-light mt-0.5 max-w-[110px] leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-50">
        <span className="text-[10px] font-medium tracking-[0.3em] uppercase text-pine-light">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-pine-light to-transparent" />
      </div>
    </section>
  );
}